import {
  Alert,
  Button,
  Card,
  DevCode,
  Loading,
  PageHeader,
  RecoveryCodes,
  TextField,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toDataURL } from 'qrcode';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { mfaStatusQuery, sessionApi, sessionQuery, useMe } from '../api/session';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';

export function AccountPage() {
  const me = useMe();
  return (
    <>
      <PageHeader title={he.account.title} />
      {me === undefined ? null : (
        <p className="mb-6 text-sm text-slate-600">
          {me.user.displayName} · <span className="ltr">{me.user.email}</span>
          {me.activeTenant === null ? null : ` · ${he.roles[me.activeTenant.role]}`}
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <ChangePassword />
        <SecondFactor />
      </div>
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const change = useMutation({
    mutationFn: sessionApi.changePassword,
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    setMismatch(next !== confirm);
    if (next === confirm) change.mutate({ currentPassword: current, newPassword: next });
  };
  return (
    <Card title={he.account.password}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <input type="text" name="username" autoComplete="username" hidden readOnly />
        <TextField
          label={he.account.current}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => {
            setCurrent(event.target.value);
          }}
        />
        <TextField
          label={he.account.next}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
        />
        <TextField
          label={he.account.confirm}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={confirm}
          error={mismatch ? he.invitation.mismatch : undefined}
          onChange={(event) => {
            setConfirm(event.target.value);
          }}
        />
        {change.isSuccess ? <Alert tone="success">{he.account.changed}</Alert> : null}
        {change.isError ? <Alert>{errorMessage(change.error)}</Alert> : null}
        <div>
          <Button type="submit" busy={change.isPending}>
            {he.common.save}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** A six-digit code field (with the development helper when the API sends the code). */
function CodeField({
  label,
  value,
  onChange,
  devCode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  devCode: string | undefined;
}) {
  return (
    <>
      <DevCode code={devCode} onUse={onChange} label={he.devCode.label} useLabel={he.devCode.use} />
      <TextField
        label={label}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        required
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="ltr"
      />
    </>
  );
}

/**
 * The second factor is optional for store staff: turn it on (QR code, then a code from the
 * app, then recovery codes shown once), renew recovery codes, or turn it off with a fresh code.
 */
function SecondFactor() {
  const queryClient = useQueryClient();
  const status = useQuery(mfaStatusQuery);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: mfaStatusQuery.queryKey });
    await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
  };

  if (status.isPending) return <Loading />;
  if (status.isError) return <Alert>{errorMessage(status.error)}</Alert>;

  return (
    <Card title={he.account.mfa}>
      <div
        className="flex flex-col gap-4 text-sm"
        data-testid="mfa-card"
        data-enabled={String(status.data.enabled)}
      >
        <p>{status.data.enabled ? he.account.mfaOn : he.account.mfaOff}</p>
        {recoveryCodes === undefined ? null : (
          <div className="flex flex-col gap-3">
            <h3 className="font-semibold">{he.account.recoveryTitle}</h3>
            <p className="text-slate-600">{he.account.recoveryIntro}</p>
            <RecoveryCodes codes={recoveryCodes} label={he.account.recoveryTitle} />
            <div>
              <Button
                tone="secondary"
                onClick={() => {
                  setRecoveryCodes(undefined);
                }}
              >
                {he.account.recoveryDone}
              </Button>
            </div>
          </div>
        )}
        {recoveryCodes !== undefined ? null : status.data.enabled ? (
          <>
            <p>
              {he.account.remaining} {status.data.recoveryCodesRemaining}
            </p>
            <Regenerate
              devCode={status.data.devCode}
              onCodes={(codes) => {
                setRecoveryCodes(codes);
                void refresh();
              }}
            />
            <Disable devCode={status.data.devCode} onDone={refresh} />
          </>
        ) : (
          <Enroll
            onCodes={(codes) => {
              setRecoveryCodes(codes);
              void refresh();
            }}
          />
        )}
      </div>
    </Card>
  );
}

function Enroll({ onCodes }: { onCodes: (codes: string[]) => void }) {
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string>();
  const setup = useMutation({ mutationFn: sessionApi.setupMfa });
  const enable = useMutation({
    mutationFn: sessionApi.enableMfa,
    onSuccess: (result) => {
      setCode('');
      onCodes(result.recoveryCodes);
    },
  });

  const uri = setup.data?.otpauthUri;
  useEffect(() => {
    if (uri === undefined) return;
    let cancelled = false;
    void toDataURL(uri, { margin: 1, width: 200 }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => {
      cancelled = true;
    };
  }, [uri]);

  if (setup.data === undefined) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-slate-600">{he.account.mfaIntro}</p>
        <div>
          <Button
            busy={setup.isPending}
            onClick={() => {
              setup.mutate();
            }}
          >
            {he.account.start}
          </Button>
        </div>
        {setup.isError ? <Alert>{errorMessage(setup.error)}</Alert> : null}
      </div>
    );
  }

  const onEnable = (event: SyntheticEvent) => {
    event.preventDefault();
    enable.mutate(code.trim());
  };
  return (
    <form onSubmit={onEnable} className="flex flex-col gap-3">
      <p className="text-slate-600">{he.account.mfaIntro}</p>
      {qr === undefined ? null : (
        <img src={qr} alt={he.account.qrAlt} width={200} height={200} className="self-center" />
      )}
      <div className="text-slate-600">
        {he.account.manual}
        <div
          className="ltr mt-1 rounded bg-slate-100 p-2 font-mono text-xs break-all select-all"
          data-testid="mfa-secret"
        >
          {setup.data.secret}
        </div>
      </div>
      <CodeField
        label={he.account.code}
        value={code}
        onChange={setCode}
        devCode={setup.data.devCode}
      />
      {enable.isError ? <Alert>{errorMessage(enable.error)}</Alert> : null}
      <div>
        <Button type="submit" busy={enable.isPending}>
          {he.account.enable}
        </Button>
      </div>
    </form>
  );
}

function Regenerate({
  devCode,
  onCodes,
}: {
  devCode: string | undefined;
  onCodes: (codes: string[]) => void;
}) {
  const [code, setCode] = useState('');
  const regenerate = useMutation({
    mutationFn: sessionApi.regenerateRecoveryCodes,
    onSuccess: (result) => {
      setCode('');
      onCodes(result.recoveryCodes);
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    regenerate.mutate(code.trim());
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 border-t border-slate-100 pt-3">
      <p className="text-slate-600">{he.account.regenerateHint}</p>
      <CodeField
        label={he.account.regenerateCode}
        value={code}
        onChange={setCode}
        devCode={devCode}
      />
      {regenerate.isError ? <Alert>{errorMessage(regenerate.error)}</Alert> : null}
      <div>
        <Button type="submit" tone="secondary" busy={regenerate.isPending}>
          {he.account.regenerate}
        </Button>
      </div>
    </form>
  );
}

function Disable({
  devCode,
  onDone,
}: {
  devCode: string | undefined;
  onDone: () => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const disable = useMutation({
    mutationFn: sessionApi.disableMfa,
    onSuccess: async () => {
      setCode('');
      await onDone();
    },
  });
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    disable.mutate(code.trim());
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 border-t border-slate-100 pt-3">
      <p className="text-slate-600">{he.account.disableHint}</p>
      <CodeField label={he.account.disableCode} value={code} onChange={setCode} devCode={devCode} />
      {disable.isError ? <Alert>{errorMessage(disable.error)}</Alert> : null}
      <div>
        <Button type="submit" tone="danger" busy={disable.isPending}>
          {he.account.disable}
        </Button>
      </div>
    </form>
  );
}
